import {Node} from "@xyflow/react";
import {
    HORIZONTAL_NODE_GAP,
    VERTICAL_NODE_GAP
} from "./FamilyTreeLayoutConstants";
import {buildChildrenByParent} from "./FamilyTreePartnerLayout";
import {getNodeWidth} from "./FamilyTreeNodeSizing";

export interface MainTreeTidyLayoutContext {
    nodes: Node[];
    parentMap: Map<string, string[]>;
    partnerMap: Map<string, string>;
    partnerRightOf: Map<string, string>;
    mainMemberIds: string[];
}

interface CompoundLayoutUnit {
    id: string;
    memberIds: string[];
    members: Node[];
    width: number;
    order: number;
    parentUnitId?: string;
    childUnitIds: string[];
    centerX: number;
    depth: number;
}

interface TidyTreeNode {
    id: string;
    unit: CompoundLayoutUnit;
    parent?: TidyTreeNode;
    children: TidyTreeNode[];
    number: number;
    prelim: number;
    mod: number;
    change: number;
    shift: number;
    ancestor: TidyTreeNode;
    thread?: TidyTreeNode;
}

const VIRTUAL_ROOT_ID = "__virtual-root__";

function getNodeOrder(nodes: Node[]) {
    return new Map(nodes.map((node, index) => [node.id, index]));
}

function getOrderedPairIds(
    memberId: string,
    partnerId: string,
    partnerRightOf: Map<string, string>,
    nodeOrder: Map<string, number>
) {
    if (partnerRightOf.get(memberId) === partnerId) {
        return [memberId, partnerId];
    }
    if (partnerRightOf.get(partnerId) === memberId) {
        return [partnerId, memberId];
    }
    const memberOrder = nodeOrder.get(memberId) ?? 0;
    const partnerOrder = nodeOrder.get(partnerId) ?? 0;
    return memberOrder <= partnerOrder ? [memberId, partnerId] : [partnerId, memberId];
}

function collectMainTreeNodeIds(
    rootIds: string[],
    nodeById: Map<string, Node>,
    childrenByParent: Map<string, string[]>,
    partnerMap: Map<string, string>
) {
    const visited = new Set<string>();
    const stack = [...rootIds.filter(id => nodeById.has(id))];

    while (stack.length > 0) {
        const currentId = stack.pop();
        if (!currentId || visited.has(currentId) || !nodeById.has(currentId)) continue;
        visited.add(currentId);

        const partnerId = partnerMap.get(currentId);
        if (partnerId && nodeById.has(partnerId) && !visited.has(partnerId)) {
            stack.push(partnerId);
        }

        const childIds = childrenByParent.get(currentId) ?? [];
        childIds.forEach(childId => {
            if (nodeById.has(childId) && !visited.has(childId)) {
                stack.push(childId);
            }
        });
    }

    return visited;
}

function buildCompoundUnits(
    mainNodeIds: Set<string>,
    nodeById: Map<string, Node>,
    nodeOrder: Map<string, number>,
    partnerMap: Map<string, string>,
    partnerRightOf: Map<string, string>
) {
    const memberToUnitId = new Map<string, string>();
    const units = new Map<string, CompoundLayoutUnit>();
    const orderedIds = Array.from(mainNodeIds).sort((left, right) => (nodeOrder.get(left) ?? 0) - (nodeOrder.get(right) ?? 0));

    orderedIds.forEach(memberId => {
        if (memberToUnitId.has(memberId)) return;
        if (!nodeById.has(memberId)) return;

        const partnerId = partnerMap.get(memberId);
        const canPair = partnerId && mainNodeIds.has(partnerId) && !memberToUnitId.has(partnerId);
        const memberIds = canPair
            ? getOrderedPairIds(memberId, partnerId, partnerRightOf, nodeOrder)
            : [memberId];
        const members = memberIds
            .map(id => nodeById.get(id))
            .filter(Boolean) as Node[];
        const width = members.length === 2
            ? getNodeWidth(members[0]) + HORIZONTAL_NODE_GAP + getNodeWidth(members[1])
            : getNodeWidth(members[0]);
        const order = Math.min(...memberIds.map(id => nodeOrder.get(id) ?? 0));
        const unitId = members.length === 2 ? memberIds.join("::") : memberId;
        const unit: CompoundLayoutUnit = {
            id: unitId,
            memberIds,
            members,
            width,
            order,
            childUnitIds: [],
            centerX: 0,
            depth: 0
        };

        units.set(unitId, unit);
        memberIds.forEach(id => memberToUnitId.set(id, unitId));
    });

    return {units, memberToUnitId};
}

function linkCompoundUnits(
    units: Map<string, CompoundLayoutUnit>,
    memberToUnitId: Map<string, string>,
    parentMap: Map<string, string[]>,
    mainNodeIds: Set<string>
) {
    const orderedUnits = Array.from(units.values()).sort((left, right) => left.order - right.order);

    orderedUnits.forEach(unit => {
        const parentCandidates = new Set<string>();

        unit.memberIds.forEach(memberId => {
            const parentIds = parentMap.get(memberId) ?? [];
            parentIds.forEach(parentId => {
                if (!mainNodeIds.has(parentId)) return;
                const parentUnitId = memberToUnitId.get(parentId);
                if (!parentUnitId || parentUnitId === unit.id) return;
                parentCandidates.add(parentUnitId);
            });
        });

        const orderedParents = Array.from(parentCandidates)
            .map(parentUnitId => units.get(parentUnitId))
            .filter(Boolean) as CompoundLayoutUnit[];
        orderedParents.sort((left, right) => left.order - right.order);
        unit.parentUnitId = orderedParents[0]?.id;
    });

    orderedUnits.forEach(unit => {
        if (!unit.parentUnitId) return;
        const parentUnit = units.get(unit.parentUnitId);
        if (!parentUnit) return;
        parentUnit.childUnitIds.push(unit.id);
    });

    orderedUnits.forEach(unit => {
        unit.childUnitIds.sort((left, right) => {
            const leftUnit = units.get(left);
            const rightUnit = units.get(right);
            return (leftUnit?.order ?? 0) - (rightUnit?.order ?? 0);
        });
    });
}

function createTidyTree(unit: CompoundLayoutUnit, units: Map<string, CompoundLayoutUnit>, parent?: TidyTreeNode, number: number = 1): TidyTreeNode {
    const node: TidyTreeNode = {
        id: unit.id,
        unit,
        parent,
        children: [],
        number,
        prelim: 0,
        mod: 0,
        change: 0,
        shift: 0,
        ancestor: undefined as unknown as TidyTreeNode
    };
    node.ancestor = node;
    node.children = unit.childUnitIds
        .map((childId, index) => {
            const childUnit = units.get(childId);
            return childUnit ? createTidyTree(childUnit, units, node, index + 1) : undefined;
        })
        .filter(Boolean) as TidyTreeNode[];
    return node;
}

function getLeftSibling(node: TidyTreeNode) {
    if (!node.parent || node.number <= 1) return undefined;
    return node.parent.children[node.number - 2];
}

function getLeftMostSibling(node: TidyTreeNode) {
    if (!node.parent || node.number <= 1) return undefined;
    return node.parent.children[0];
}

function nextLeft(node?: TidyTreeNode) {
    if (!node) return undefined;
    return node.children[0] ?? node.thread;
}

function nextRight(node?: TidyTreeNode) {
    if (!node) return undefined;
    return node.children[node.children.length - 1] ?? node.thread;
}

function getUnitSeparation(left: TidyTreeNode, right: TidyTreeNode) {
    return left.unit.width / 2 + HORIZONTAL_NODE_GAP + right.unit.width / 2;
}

function moveSubtree(left: TidyTreeNode, right: TidyTreeNode, shift: number) {
    const subtreeCount = right.number - left.number;
    if (subtreeCount <= 0) return;
    right.change -= shift / subtreeCount;
    right.shift += shift;
    left.change += shift / subtreeCount;
    right.prelim += shift;
    right.mod += shift;
}

function executeShifts(node: TidyTreeNode) {
    let shift = 0;
    let change = 0;

    for (let index = node.children.length - 1; index >= 0; index -= 1) {
        const child = node.children[index];
        child.prelim += shift;
        child.mod += shift;
        change += child.change;
        shift += child.shift + change;
    }
}

function resolveAncestor(innerLeft: TidyTreeNode, node: TidyTreeNode, defaultAncestor: TidyTreeNode) {
    return innerLeft.ancestor.parent === node.parent ? innerLeft.ancestor : defaultAncestor;
}

function apportion(node: TidyTreeNode, defaultAncestor: TidyTreeNode) {
    const leftSibling = getLeftSibling(node);
    const leftMostSibling = getLeftMostSibling(node);
    if (!leftSibling || !leftMostSibling) {
        return defaultAncestor;
    }

    let innerRight: TidyTreeNode = node;
    let outerRight: TidyTreeNode = node;
    let innerLeft: TidyTreeNode = leftSibling;
    let outerLeft: TidyTreeNode = leftMostSibling;
    let innerRightMod = innerRight.mod;
    let outerRightMod = outerRight.mod;
    let innerLeftMod = innerLeft.mod;
    let outerLeftMod = outerLeft.mod;

    while (nextRight(innerLeft) && nextLeft(innerRight)) {
        innerLeft = nextRight(innerLeft)!;
        innerRight = nextLeft(innerRight)!;
        outerLeft = nextLeft(outerLeft)!;
        outerRight = nextRight(outerRight)!;
        outerRight.ancestor = node;

        const shift = (innerLeft.prelim + innerLeftMod + getUnitSeparation(innerLeft, innerRight)) - (innerRight.prelim + innerRightMod);
        if (shift > 0) {
            const ancestor = resolveAncestor(innerLeft, node, defaultAncestor);
            moveSubtree(ancestor, node, shift);
            innerRightMod += shift;
            outerRightMod += shift;
        }

        innerLeftMod += innerLeft.mod;
        innerRightMod += innerRight.mod;
        outerLeftMod += outerLeft.mod;
        outerRightMod += outerRight.mod;
    }

    if (nextRight(innerLeft) && !nextRight(outerRight)) {
        outerRight.thread = nextRight(innerLeft);
        outerRight.mod += innerLeftMod - outerRightMod;
    } else if (nextLeft(innerRight) && !nextLeft(outerLeft)) {
        outerLeft.thread = nextLeft(innerRight);
        outerLeft.mod += innerRightMod - outerLeftMod;
        defaultAncestor = node;
    }

    return defaultAncestor;
}

function firstWalk(node: TidyTreeNode) {
    if (node.children.length === 0) {
        const leftSibling = getLeftSibling(node);
        node.prelim = leftSibling ? leftSibling.prelim + getUnitSeparation(leftSibling, node) : 0;
        return;
    }

    let defaultAncestor = node.children[0];
    node.children.forEach(child => {
        firstWalk(child);
        defaultAncestor = apportion(child, defaultAncestor);
    });
    executeShifts(node);

    const firstChild = node.children[0];
    const lastChild = node.children[node.children.length - 1];
    const midpoint = (
        (firstChild.prelim - firstChild.unit.width / 2)
        + (lastChild.prelim + lastChild.unit.width / 2)
    ) / 2;
    const leftSibling = getLeftSibling(node);

    if (leftSibling) {
        node.prelim = leftSibling.prelim + getUnitSeparation(leftSibling, node);
        node.mod = node.prelim - midpoint;
        return;
    }

    node.prelim = midpoint;
}

function secondWalk(node: TidyTreeNode, modSum: number, depth: number) {
    node.unit.centerX = node.prelim + modSum;
    node.unit.depth = depth;
    node.children.forEach(child => secondWalk(child, modSum + node.mod, depth + 1));
}

function shiftSubtree(node: TidyTreeNode, delta: number) {
    node.unit.centerX += delta;
    node.children.forEach(child => shiftSubtree(child, delta));
}

function getSubtreeBounds(node: TidyTreeNode) {
    let left = node.unit.centerX - node.unit.width / 2;
    let right = node.unit.centerX + node.unit.width / 2;

    node.children.forEach(child => {
        const childBounds = getSubtreeBounds(child);
        left = Math.min(left, childBounds.left);
        right = Math.max(right, childBounds.right);
    });

    return {left, right};
}

function getChildrenBounds(node: TidyTreeNode) {
    const childBounds = node.children.map(getSubtreeBounds);
    return {
        left: Math.min(...childBounds.map(bounds => bounds.left)),
        right: Math.max(...childBounds.map(bounds => bounds.right))
    };
}

function centerNodeOverChildren(node: TidyTreeNode) {
    if (node.children.length === 0) return;
    const {left, right} = getChildrenBounds(node);
    node.unit.centerX = (left + right) / 2;
}

function alignAncestorsToChildren(node: TidyTreeNode) {
    node.children.forEach(child => alignAncestorsToChildren(child));
    centerNodeOverChildren(node);
}

function enforceSiblingSubtreeSpacing(node: TidyTreeNode) {
    node.children.forEach(child => enforceSiblingSubtreeSpacing(child));
    if (node.children.length < 2) {
        centerNodeOverChildren(node);
        return;
    }

    for (let leftIndex = 0; leftIndex < node.children.length - 1; leftIndex += 1) {
        const leftChild = node.children[leftIndex];
        const leftBounds = getSubtreeBounds(leftChild);

        for (let rightIndex = leftIndex + 1; rightIndex < node.children.length; rightIndex += 1) {
            const rightChild = node.children[rightIndex];
            const rightBounds = getSubtreeBounds(rightChild);
            const gap = rightBounds.left - leftBounds.right;
            if (gap >= HORIZONTAL_NODE_GAP) continue;

            const delta = HORIZONTAL_NODE_GAP - gap;
            for (let shiftIndex = rightIndex; shiftIndex < node.children.length; shiftIndex += 1) {
                shiftSubtree(node.children[shiftIndex], delta);
            }
        }
    }

    centerNodeOverChildren(node);
}

function getUnitBounds(units: CompoundLayoutUnit[]) {
    const left = Math.min(...units.map(unit => unit.centerX - unit.width / 2));
    const right = Math.max(...units.map(unit => unit.centerX + unit.width / 2));
    return {left, right, center: (left + right) / 2};
}

function getCenteringTargetUnits(units: CompoundLayoutUnit[], mainMemberIds: string[]) {
    const mainIdSet = new Set(mainMemberIds);
    const focusUnits = units.filter(unit => unit.memberIds.some(memberId => mainIdSet.has(memberId)));
    return focusUnits.length > 0 ? focusUnits : units;
}

function translateUnits(units: CompoundLayoutUnit[], deltaX: number) {
    if (!Number.isFinite(deltaX) || Math.abs(deltaX) < 0.0001) return;
    units.forEach(unit => {
        unit.centerX += deltaX;
    });
}

function applyFinalMainTreeTranslation(units: CompoundLayoutUnit[], mainMemberIds: string[]) {
    const unitsToCenter = getCenteringTargetUnits(units, mainMemberIds);
    if (unitsToCenter.length === 0) return;
    const {center} = getUnitBounds(unitsToCenter);
    translateUnits(units, -center);
}

function projectUnitPositions(unit: CompoundLayoutUnit) {
    if (unit.members.length === 1) {
        unit.members[0].position.x = unit.centerX;
        unit.members[0].position.y = unit.depth * VERTICAL_NODE_GAP;
        return;
    }

    const [leftNode, rightNode] = unit.members;
    const leftWidth = getNodeWidth(leftNode);
    const rightWidth = getNodeWidth(rightNode);
    const leftEdge = unit.centerX - unit.width / 2;

    leftNode.position.x = leftEdge + leftWidth / 2;
    leftNode.position.y = unit.depth * VERTICAL_NODE_GAP;
    rightNode.position.x = leftEdge + leftWidth + HORIZONTAL_NODE_GAP + rightWidth / 2;
    rightNode.position.y = unit.depth * VERTICAL_NODE_GAP;
}

export function applyMainTreeTidyLayout(context: MainTreeTidyLayoutContext): Set<string> {
    const familyNodes = context.nodes.filter(node => node.type === "familyMember");
    const nodeById = new Map(familyNodes.map(node => [node.id, node]));
    const childrenByParent = buildChildrenByParent(context.parentMap);
    const mainNodeIds = collectMainTreeNodeIds(context.mainMemberIds, nodeById, childrenByParent, context.partnerMap);
    if (mainNodeIds.size === 0) return mainNodeIds;

    const nodeOrder = getNodeOrder(context.nodes);
    const {units, memberToUnitId} = buildCompoundUnits(mainNodeIds, nodeById, nodeOrder, context.partnerMap, context.partnerRightOf);
    if (units.size === 0) return mainNodeIds;

    linkCompoundUnits(units, memberToUnitId, context.parentMap, mainNodeIds);

    const orderedUnits = Array.from(units.values()).sort((left, right) => left.order - right.order);
    const rootUnits = orderedUnits.filter(unit => !unit.parentUnitId);
    if (rootUnits.length === 0) return mainNodeIds;

    const virtualRootUnit: CompoundLayoutUnit = {
        id: VIRTUAL_ROOT_ID,
        memberIds: [],
        members: [],
        width: 0,
        order: -1,
        childUnitIds: rootUnits.map(unit => unit.id),
        centerX: 0,
        depth: -1
    };
    const virtualRoot = createTidyTree(virtualRootUnit, units);

    firstWalk(virtualRoot);
    virtualRoot.children.forEach(child => secondWalk(child, -virtualRoot.prelim, 0));
    enforceSiblingSubtreeSpacing(virtualRoot);
    virtualRoot.children.forEach(child => alignAncestorsToChildren(child));

    // Tidy builds local structure first; the final pass only places the finished tree in the global frame.
    applyFinalMainTreeTranslation(orderedUnits, context.mainMemberIds);
    orderedUnits.forEach(projectUnitPositions);
    return mainNodeIds;
}